import { useState } from "react";
import { api, formatError, BRAND } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Instagram, Facebook } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! We'll respond soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-5">
        <span className="font-script text-3xl text-brand-coral">Say hello</span>
        <h1 className="font-display text-4xl md:text-5xl font-extrabold text-brand-navy tracking-tight mt-2">Contact Us</h1>
        <p className="mt-4 text-slate-600 leading-relaxed">We reply to every inquiry, usually within one business day. Prefer email or phone? Both work great.</p>

        <div className="mt-8 space-y-4">
          <a href={`mailto:${BRAND.email}`} className="flex items-start gap-4 p-4 bg-brand-surface rounded-2xl hover:bg-brand-teal/10 transition-colors" data-testid="contact-email">
            <Mail className="h-6 w-6 text-brand-teal mt-1" />
            <div><div className="font-display font-bold text-brand-navy">Email</div><div className="text-slate-600">{BRAND.email}</div></div>
          </a>
          <a href={`tel:${BRAND.phone}`} className="flex items-start gap-4 p-4 bg-brand-surface rounded-2xl hover:bg-brand-teal/10 transition-colors" data-testid="contact-phone">
            <Phone className="h-6 w-6 text-brand-teal mt-1" />
            <div><div className="font-display font-bold text-brand-navy">Phone</div><div className="text-slate-600">{BRAND.phone}</div></div>
          </a>
          <div className="flex items-start gap-4 p-4 bg-brand-surface rounded-2xl">
            <MapPin className="h-6 w-6 text-brand-teal mt-1" />
            <div><div className="font-display font-bold text-brand-navy">Training Center</div><div className="text-slate-600">{BRAND.address}</div></div>
          </div>
          <div className="flex gap-3 pt-2">
            <a href={BRAND.instagram} target="_blank" rel="noreferrer" className="p-3 bg-brand-navy text-white rounded-full hover:bg-brand-teal transition-colors" data-testid="contact-instagram"><Instagram className="h-5 w-5" /></a>
            <a href={BRAND.facebook} target="_blank" rel="noreferrer" className="p-3 bg-brand-navy text-white rounded-full hover:bg-brand-teal transition-colors" data-testid="contact-facebook"><Facebook className="h-5 w-5" /></a>
          </div>
        </div>
      </div>

      <form onSubmit={submit} className="lg:col-span-7 bg-white border border-slate-200 rounded-3xl p-8 space-y-5" data-testid="contact-form">
        <h2 className="font-display text-2xl font-bold text-brand-navy tracking-tight">Send a message</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div><Label className="text-brand-navy font-semibold">Name *</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-brand-surface border-slate-200 rounded-lg" data-testid="contact-name" /></div>
          <div><Label className="text-brand-navy font-semibold">Email *</Label><Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 bg-brand-surface border-slate-200 rounded-lg" data-testid="contact-email-input" /></div>
          <div className="md:col-span-2"><Label className="text-brand-navy font-semibold">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 bg-brand-surface border-slate-200 rounded-lg" data-testid="contact-phone-input" /></div>
        </div>
        <div><Label className="text-brand-navy font-semibold">Message *</Label><Textarea required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1 bg-brand-surface border-slate-200 rounded-lg" data-testid="contact-message" /></div>
        <Button type="submit" disabled={submitting} className="w-full bg-brand-coral hover:bg-brand-coral-dark text-white rounded-full py-6 font-bold text-base btn-lift" data-testid="contact-submit">
          {submitting ? "Sending..." : "Send Message"}
        </Button>
      </form>
    </div>
  );
}
